import { useEffect, useMemo, useState } from "react";
import { Virtuoso } from 'react-virtuoso';
import { API_ROUTES } from "../../../core/Constants";
import { hasRole, selectChat, useChatList, useGlobal, useHub, useScreen } from "../../../core/Global";
import ChatContactRow from "./ChatContactRow";
import ChatContactItem from "../models/ChatContactItem";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import ChatMessage from "../models/ChatMessage";
import ChatSearchResultRow from "./ChatSearchResultRow";
import Tagify from "../../../components/tagify/Tagify";
import ChatGeneralSearch from "./ChatGeneralSearch";
import Indicator from "../../../components/base/Indicator";
import ChatSpecificSearch from "./ChatSpecificSearch";
import { CHAT_BLOCK_CHANGED_EVENT } from "./ChatContactContextMenu";

const BLOCKED_ROW_HEIGHT = 37;

interface IProps {
    readedChatId?: string
    //refresh?: boolean
    reset?: boolean
    searchingChat?: ChatContactItem
    onSelectContact?: (e: ChatContactItem) => void
    onSearchingChatCleared?: () => void
    onNewChat: () => void
}

export default function ChatContactsBox(props: IProps) {

    let searchChatInput: HTMLInputElement | null;

    const hub = useHub()
    const screen = useScreen()
    const [contactsFiltered, contactsFilteredSet] = useState<ChatContactItem[]>();

    const [globalState, globalStateSet] = useGlobal()

    //const [items, itemsSet] = useState<ChatContactItem[]>([]);

    // Archived chats are paged (fixed 100 per page) and appended as the user scrolls down.
    // These only apply while the archived view is open; search reuses contactsFiltered too, so
    // archivedMode guards endReached from paginating over search results.
    const ARCHIVED_PAGE_SIZE = 100;
    const [archivedMode, archivedModeSet] = useState<boolean>(false);
    const [archivedPage, archivedPageSet] = useState<number>(1);
    const [archivedHasMore, archivedHasMoreSet] = useState<boolean>(false);
    const [archivedLoadingMore, archivedLoadingMoreSet] = useState<boolean>(false);

    // Blocked chats are archived too, but listed apart: a thin "Blocked" row at the top of the
    // archived view opens them (same paging), and the same row leads back.
    const [blockedMode, blockedModeSet] = useState<boolean>(false);
    const [blockedCount, blockedCountSet] = useState<number>(0);

    useEffect(() => {
        if (!archivedMode) return;
        const onChange = () => hub.Get(API_ROUTES.Chat + "blockedcount", res => {
            if (res.success) blockedCountSet(res.data ?? 0);
        });
        onChange();
        window.addEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange);
        return () => window.removeEventListener(CHAT_BLOCK_CHANGED_EVENT, onChange);
    }, [archivedMode])

    const items = useMemo(() => {
        let base = contactsFiltered ?? globalState.chatlist ?? [];

        // A chat leaves the archive as soon as a message lands in it, and the server pushes it into
        // the live chat list. Drop those from the archived view so an unarchived chat doesn't linger
        // there (and isn't listed twice once the view is closed).
        if (archivedMode) {
            base = base.filter(f => !globalState.chatlist?.some(c => c.chatId == f.chatId));
        }

        // The Office and Rinvox chats sort like any other: pinned first, then by last message.
        return [...base].sort((a, b) => {
            if (a.pinned && !b.pinned) return -1;
            if (!a.pinned && b.pinned) return 1;

            const dateA = new Date(a.lastUpdateDate!).getTime();
            const dateB = new Date(b.lastUpdateDate!).getTime();
            return dateB - dateA;
        });

    }, [globalState.chatlist, contactsFiltered, archivedMode]);

    // useEffect(() => {
    //     const tmp = (contactsFiltered ? contactsFiltered :
    //         (chatlist ?? []))
    //         .sort((a, b) => {
    //             if (a.pinned && !b.pinned) return -1;
    //             if (!a.pinned && b.pinned) return 1;

    //             const dateA = new Date(a.lastUpdateDate!).getTime();
    //             const dateB = new Date(b.lastUpdateDate!).getTime();
    //             return dateB - dateA;

    //         });

    //     itemsSet(tmp);

    // }, [chatlist, contactsFiltered])

    const [keyword, keywordSet] = useState<string>("");
    const [keywordTyping, keywordTypingSet] = useState<boolean>(false);






    const [chatSearching, chatSearchingSet] = useState<boolean>(false);

    const [chatSearched, chatSearchedSet] = useState<boolean>(false);
    const [searchResult, searchResultSet] = useState<ChatMessage[]>([]);

    const [selectedSearchedMessage, selectedSearchedMessageSet] = useState<ChatMessage>();

    useEffect(() => {

    }, [selectedSearchedMessage])



    const [boxHeight, boxHeightSet] = useState<number>();
    const [chatSearchBoxHeight, chatSearchBoxHeightSet] = useState<number>();

    const fixSizes = () => {
        var w = screen.width;
        var h = screen.height;
        h = h - ($("#kt_header").outerHeight() ?? 50);
        h = h - ($("#kt_chat_contacts_header").outerHeight() ?? 50);
        h = h - ($("#contacts-searching-advanced").outerHeight() ?? 0);

        boxHeightSet(h);

        h = h - ($("#chat_search_messages_header").outerHeight() ?? 50);
        chatSearchBoxHeightSet(h);

    }




    useEffect(() => {

        fixSizes();
        window.addEventListener("resize", fixSizes);



        return () => {
            window.removeEventListener("resize", fixSizes);
        }

    }, [])


    const [chatsLoading, chatsLoadingSet] = useState<boolean>(false);

    useEffect(() => {
        if (globalState.chatsFetched) chatsLoadingSet(false)
        else chatsLoadingSet(true)
    }, [globalState.chatsFetched])


    // Sending a message in the open chat takes the sidebar back to the live chat list: search
    // results and the archived pages are dropped, so the chat we just wrote in shows up there
    // (the server has unarchived it and pushes it over the live hub).
    useEffect(() => {
        if (!props.reset) return;
        archivedModeSet(false);
        blockedModeSet(false);
        archivedHasMoreSet(false);
        archivedLoadingMoreSet(false);
        archivedPageSet(1);
        contactsFilteredSet(undefined);
    }, [props.reset])

    const loadArchivedPage = (page: number, blocked: boolean = blockedMode) => {
        hub.Get(API_ROUTES.Chat + (blocked ? "blocked" : "archived") + "?page=" + page, res => {
            const data: ChatContactItem[] = res.data ?? [];
            if (page <= 1) contactsFilteredSet(data);
            else contactsFilteredSet(prev => [...(prev ?? []), ...data]);
            archivedPageSet(page);
            archivedHasMoreSet(data.length >= ARCHIVED_PAGE_SIZE);
            chatsLoadingSet(false);
            archivedLoadingMoreSet(false);
        }, () => {
            chatsLoadingSet(false);
            archivedLoadingMoreSet(false);
        });
    }

    const loadMoreArchived = () => {
        if (!archivedMode || !archivedHasMore || archivedLoadingMore) return;
        archivedLoadingMoreSet(true);
        loadArchivedPage(archivedPage + 1);
    }

    const openArchivedList = (blocked: boolean) => {
        blockedModeSet(blocked);
        archivedPageSet(1);
        archivedHasMoreSet(false);
        chatsLoadingSet(true);
        loadArchivedPage(1, blocked);
    }

    const showBlockedRow = archivedMode && (blockedMode || blockedCount > 0);


    return (
        <div className="card card-flush" style={{ borderRadius: 0 }}>

            <div className="card-header p-2" id="kt_chat_contacts_header"
                style={{
                    minHeight: "auto"
                }}>
                {
                    props.searchingChat?.chatId ?
                        <ChatSpecificSearch chatId={props.searchingChat.chatId}
                            onSearch={data => searchResultSet(data)} />
                        :
                        <ChatGeneralSearch
                            clear={props.reset}
                            onSearch={data => {
                                // A search takes over the filtered slot; leave archived paging mode.
                                archivedModeSet(false)
                                blockedModeSet(false)
                                archivedHasMoreSet(false)
                                contactsFilteredSet(data)
                            }}
                            onNewChat={props.onNewChat}
                            onShowArchived={show => {
                                if (show) {
                                    archivedModeSet(true)
                                    openArchivedList(false)
                                }
                                else {
                                    archivedModeSet(false)
                                    blockedModeSet(false)
                                    archivedHasMoreSet(false)
                                    chatsLoadingSet(false)
                                    contactsFilteredSet(undefined)
                                }
                            }} />
                }

            </div>

            <div className="card-body p-0" id="kt_chat_contacts_body">
                {
                    props.searchingChat ?
                        <div>
                            <div id="chat_search_messages_header">
                                <div className="py-2 px-3 fs-7"
                                    style={{
                                        backgroundColor: "#f0f0f0"
                                    }}>
                                    Search messages in
                                </div>
                                <div className={"d-flex align-items-start flex-stack py-2 px-3"}>
                                    <FlexRow className="w-100">
                                        {
                                            props.searchingChat.avatar ?
                                                <div className="symbol symbol-45px symbol-circle">
                                                    <img alt={props.searchingChat.title} src={props.searchingChat.avatar} />
                                                </div>
                                                :
                                                <div className="symbol symbol-45px symbol-circle">
                                                    <span className="symbol-label bg-light-danger text-danger fs-6 fw-bolder">{props.searchingChat.avatarAlphabet}</span>
                                                </div>
                                        }

                                        <div className="ms-2 flex-grow-1">
                                            <FlexRow>
                                                <div className={"fs-6 text-gray-900"}>
                                                    {props.searchingChat.title}
                                                </div>
                                                <div className="ms-auto me-3">
                                                    <a href="#" onClick={e => {
                                                        e.preventDefault();
                                                        searchResultSet([]);
                                                        if (props.onSearchingChatCleared) props.onSearchingChatCleared();
                                                    }}>
                                                        <Icon icon="times" className="text-dark" size={16} type="light" />
                                                    </a>
                                                </div>
                                            </FlexRow>
                                        </div>

                                    </FlexRow>
                                </div>
                                <div className="py-2 px-3 fs-7"
                                    style={{
                                        backgroundColor: "#f0f0f0"
                                    }}>
                                    {
                                        chatSearching ?
                                            <FlexRow>
                                                <span className="me-2"><Icon icon="spinner-third" type="light" spin size={12} /></span>
                                                <span>Searching...</span>
                                            </FlexRow>
                                            : chatSearched && searchResult.length > 0 ?
                                                <span>
                                                    {searchResult.length} Messages found!
                                                </span>
                                                : chatSearched && searchResult.length == 0 ?
                                                    <span>
                                                        No messages found!
                                                    </span>
                                                    :
                                                    <span>
                                                        Search result
                                                    </span>

                                    }
                                </div>
                            </div>
                            <div className="scroll-y"
                                style={{
                                    height: chatSearchBoxHeight
                                }}>

                                {
                                    searchResult.map((msg, key) =>
                                        <ChatSearchResultRow key={key} item={msg}
                                            selected={selectedSearchedMessage?.id == msg.id}
                                            chat={props.searchingChat!}
                                            onClick={m => selectedSearchedMessageSet(m)} />
                                    )
                                }

                            </div>
                        </div>
                        : true ?
                            <div>
                                {
                                    chatsLoading ?
                                        <div className="pt-10" style={{ height: boxHeight }}>
                                            <Indicator />
                                        </div>
                                        : (globalState.chatlist?.length == 0 && !contactsFiltered && hasRole("ChatStart")) ?
                                            <div className="text-center py-10" style={{ height: boxHeight }}>
                                                <button className="btn btn-sm btn-primary" onClick={props.onNewChat}>
                                                    <Icon icon="edit" />
                                                    Start New Chat
                                                </button>
                                            </div>
                                            :
                                            <>
                                                {
                                                    showBlockedRow &&
                                                    <a href="#" className="d-flex align-items-center px-4 border-bottom text-gray-700 bg-light text-hover-primary fs-7"
                                                        style={{ height: BLOCKED_ROW_HEIGHT }}
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            openArchivedList(!blockedMode);
                                                        }}>
                                                        <Icon icon={blockedMode ? "arrow-left" : "ban"} className="me-2" size={12} />
                                                        <span className="fw-bold">{blockedMode ? "Spam chats" : "Spam"}</span>
                                                        <span className="badge badge-light-dark ms-2">{blockedCount}</span>
                                                        {
                                                            !blockedMode &&
                                                            <Icon icon="chevron-right" className="ms-auto" size={10} />
                                                        }
                                                    </a>
                                                }
                                                <Virtuoso
                                                    style={{ height: (boxHeight ?? 0) - (showBlockedRow ? BLOCKED_ROW_HEIGHT : 0) }}
                                                    totalCount={items.length}
                                                    endReached={() => loadMoreArchived()}
                                                    components={{
                                                        Footer: () => archivedMode && archivedLoadingMore ?
                                                            <div className="py-4"><Indicator /></div> : null
                                                    }}
                                                    itemContent={(index) => {
                                                        const item = items[index];
                                                        return (
                                                            <ChatContactRow key={item.id} item={item}
                                                                seperator={index + 1 < (items ?? []).length}
                                                                readed={item.chatId == props.readedChatId}
                                                                onUnarchive={() => {
                                                                    // Only this row leaves the archived view (or the search
                                                                    // result); it shows up in the live list on its own.
                                                                    contactsFilteredSet(prev => prev?.filter(f => f.chatId != item.chatId));
                                                                }}
                                                                onBlockChange={() => {
                                                                    // Blocked from the archive, or unblocked from the
                                                                    // blocked list: the row leaves the view it is in.
                                                                    if (archivedMode) contactsFilteredSet(prev => prev?.filter(f => f.chatId != item.chatId));
                                                                }}
                                                            />
                                                        );
                                                    }}
                                                />
                                            </>
                                }
                            </div>
                            :
                            <div>
                                <div className="scroll-y"
                                    style={{
                                        height: boxHeight
                                    }}>

                                    {
                                        globalState.chatlist?.length == 0 && !contactsFiltered && hasRole("ChatStart") && !chatsLoading &&
                                        <div className="text-center py-10">
                                            <button className="btn btn-sm btn-primary" onClick={props.onNewChat}>
                                                <Icon icon="edit" />
                                                Start New Chat
                                            </button>
                                        </div>
                                    }

                                    {
                                        chatsLoading ?
                                            <div className="pt-10" style={{ height: boxHeight }}>
                                                <Indicator />
                                            </div>
                                            :
                                            <>
                                                {
                                                    items
                                                        .map((item, key) =>
                                                            <ChatContactRow key={item.id} item={item}
                                                                seperator={key + 1 < (globalState.chatlist ?? []).length}
                                                                readed={item.chatId == props.readedChatId}
                                                                onUnarchive={() => {
                                                                    contactsFilteredSet(prev => prev?.filter(f => f.chatId != item.chatId));
                                                                }}
                                                            />
                                                        )
                                                }
                                            </>

                                    }



                                </div>
                            </div>
                }

            </div>
        </div>
    )
}